import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Coffee, CupSoda, Cookie, Salad, Flame, Search, Snowflake, Star, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useCart } from '../contexts/CartContext';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categoryIcons = {
  'snowflake': Snowflake,
  'coffee': Coffee,
  'cup-soda': CupSoda,
  'cookie': Cookie,
  'salad': Salad,
  'flame': Flame
};

export default function Menu() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, prodRes] = await Promise.all([
          fetch(`${API}/categories`),
          fetch(`${API}/products`)
        ]);
        
        const catData = await catRes.json();
        const prodData = await prodRes.json();
        
        // Sort categories by order field (Bebidas first)
        const sortedCategories = [...catData].sort((a, b) => (a.order || 99) - (b.order || 99));
        setCategories(sortedCategories);
        setProducts(prodData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const filteredProducts = products.filter(p => {
    const matchesCategory = !selectedCategory || p.category_id === selectedCategory;
    const matchesSearch = !searchQuery || 
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleAddToCart = (product) => {
    addItem(product);
    toast.success(`${product.name} agregado al carrito`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-emerald-700 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      
      <main className="pt-24 pb-16 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-10">
            <h1 className="text-4xl font-bold text-stone-900 mb-4">Nuestro Menú</h1>
            <p className="text-stone-600 text-lg">Comida fresca y saludable para tu día de trabajo</p>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
              <Input
                placeholder="Buscar productos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 bg-white border-stone-200 rounded-xl h-12"
                data-testid="search-input"
              />
            </div>
          </div>

          {/* Categories */}
          <div className="flex flex-wrap gap-3 mb-10">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-5 py-2.5 rounded-full font-medium transition-all text-sm ${
                !selectedCategory
                  ? 'bg-emerald-700 text-white shadow-lg shadow-emerald-700/20'
                  : 'bg-white text-stone-700 border border-stone-200 hover:bg-stone-50'
              }`}
              data-testid="menu-category-all"
            >
              Todos
            </button>
            {categories.map(cat => {
              const IconComponent = categoryIcons[cat.icon] || Coffee;
              return (
                <button
                  key={cat.category_id}
                  onClick={() => setSelectedCategory(cat.category_id)}
                  className={`px-5 py-2.5 rounded-full font-medium transition-all flex items-center gap-2 text-sm ${
                    selectedCategory === cat.category_id
                      ? 'bg-emerald-700 text-white shadow-lg shadow-emerald-700/20'
                      : 'bg-white text-stone-700 border border-stone-200 hover:bg-stone-50'
                  }`}
                  data-testid={`menu-category-${cat.category_id}`}
                >
                  <IconComponent className="w-4 h-4" />
                  {cat.name_es}
                </button>
              );
            })}
          </div>

          {/* Products Grid */}
          {filteredProducts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-stone-500 text-lg">No se encontraron productos</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map((product, index) => {
                const finalPrice = product.promotion_active && product.discount_price 
                  ? product.discount_price 
                  : product.promotion_active && product.discount_percent 
                    ? product.price * (1 - product.discount_percent / 100)
                    : product.price;
                const hasDiscount = finalPrice < product.price;
                
                return (
                <div
                  key={product.product_id}
                  className="group bg-white rounded-2xl border border-stone-100 overflow-hidden hover:border-emerald-200 hover:shadow-lg transition-all duration-300 animate-fade-in cursor-pointer"
                  style={{ animationDelay: `${index * 0.03}s` }}
                  data-testid={`menu-product-${product.product_id}`}
                  onClick={() => navigate(`/product/${product.product_id}`)}
                >
                  <div className="aspect-square overflow-hidden bg-stone-100 relative">
                    <img
                      src={product.image_url || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400'}
                      alt={product.name}
                      className="w-full h-full object-cover product-card-image group-hover:scale-105 transition-transform"
                    />
                    {/* Badges */}
                    <div className="absolute top-2 left-2 flex flex-col gap-1">
                      {product.promotion_active && (
                        <Badge className="bg-orange-500 text-white border-0 text-xs">
                          {product.promotion_label || `${product.discount_percent}% OFF`}
                        </Badge>
                      )}
                      {product.featured && (
                        <Badge className="bg-yellow-500 text-white border-0 text-xs">
                          <Star className="w-3 h-3 mr-1 fill-current" /> Destacado
                        </Badge>
                      )}
                    </div>
                    {/* View Details Button */}
                    <button
                      className="absolute bottom-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/product/${product.product_id}`);
                      }}
                    >
                      <Eye className="w-4 h-4 text-stone-600" />
                    </button>
                  </div>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-semibold text-stone-900 group-hover:text-emerald-700 transition-colors">
                        {product.name}
                      </h3>
                      {product.temperature && (
                        <Badge variant="secondary" className="text-xs bg-stone-100 text-stone-600 border-0 shrink-0">
                          {product.temperature}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-stone-500 mb-2 line-clamp-2">{product.description}</p>
                    {product.weight && (
                      <p className="text-xs text-stone-400 mb-4">{product.weight}</p>
                    )}
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-xl font-bold text-orange-500">
                          ${finalPrice.toFixed(2)}
                        </span>
                        {hasDiscount && (
                          <span className="text-sm text-stone-400 line-through ml-2">
                            ${product.price.toFixed(2)}
                          </span>
                        )}
                      </div>
                      <Button
                        size="sm"
                        className="bg-emerald-700 hover:bg-emerald-800 rounded-full px-4"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddToCart({...product, price: finalPrice});
                        }}
                        data-testid={`menu-add-${product.product_id}`}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Agregar
                      </Button>
                    </div>
                  </div>
                </div>
              );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
